<template>
  <div class="logs-header" data-testid="logs-panel-header">
    <div class="logs-header__titles">
      <h2 class="logs-header__title">Logs</h2>
      <p class="logs-header__subtitle">{{ categoryCount }} categories</p>
    </div>

    <div class="logs-header__actions">
      <div class="logs-header__autoscroll">
        <span class="logs-header__autoscroll-label">Auto-scroll</span>
        <FormKit
          type="toggle"
          :model-value="autoScroll"
          outer-class="!mb-0"
          data-testid="logs-autoscroll-toggle"
          @update:model-value="$emit('toggleAutoScroll')"
        />
      </div>

      <Button
        v-if="insightsURL"
        type="solid"
        variant="gray"
        icon="general.downloadFile"
        text="Download logs"
        :href="insightsURL"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="logs-download"
      />

      <Button
        v-if="isCurrentItemOwner"
        type="solid"
        variant="blue"
        icon="general.bug"
        text="Create a ticket"
        data-testid="logs-create-ticket"
        @click="$emit('createTicket')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Button } from '@/storybook';
import { useArtifactsStore } from '@/modules/viewer/store/useArtifactsStore';
import { useUser } from '@/modules/viewer/composables/useUser';

defineProps<{
  autoScroll: boolean;
  categoryCount: number;
}>();

defineEmits<{
  createTicket: [];
  toggleAutoScroll: [];
}>();

const { insightsURL } = storeToRefs(useArtifactsStore());
// Only owners can create a support ticket (the action opens the share modal's
// Export tab, which has owner-only surfaces). Same-org team members still see
// everything else — Auto-scroll, Download logs, the log entries themselves.
const { isCurrentItemOwner } = useUser();
</script>

<style scoped>
.logs-header {
  @apply flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3;
}

.logs-header__titles {
  @apply min-w-0;
}

.logs-header__title {
  @apply text-xl font-bold text-zinc-900 dark:text-zinc-100 leading-tight truncate;
}

.logs-header__subtitle {
  @apply text-xs font-medium text-zinc-500 dark:text-zinc-400;
}

.logs-header__actions {
  @apply flex flex-wrap items-center gap-2;
}

.logs-header__autoscroll {
  @apply flex items-center gap-2;
}

.logs-header__autoscroll-label {
  @apply text-sm font-medium text-zinc-600 dark:text-zinc-400 whitespace-nowrap;
}
</style>
